import React from "react";
import { KeyboardArrowDown } from "@mui/icons-material";

import TranslatableContentEditor from "modules/common/components/authoring/translatableContentEditor";
import { ICustomCssModelv1 } from "api/files";
import { PromptTypeEnum } from "modules/smartContent/models";
import LanguageBarWithTranslateDialog from "modules/common/components/languageBarWithTranslateDialog";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import { useLanguagesData } from "modules/common/hooks/data/useLanguages";
import { TranslationContentType } from "modules/common/models";
import { defaultTranslateContentId, ILanguage, ILcidTranslateDetails } from "modules/localization/models";
import { DEFAULT_TRANSLATABLE_CONTENT, TranslatableContent, TranslatedContent } from "modules/common/components/authoring/models";
import TranslateAllIssueDialog from "modules/settings/components/translateAllIssueDialog";
import useTranslateAll from "modules/common/hooks/data/useTranslateAll";
import { cognitiveServicesApi, draftPostsApi } from "../../../../../api/instances";

import "../styles/postMainPanel.sass";
import ErrorSnackbar from "modules/common/components/snackbars/errorSnackbar";

interface IPostMainPanelProps {
    postId?: string;
    translations?: TranslatedContent;
    highlightedText?: string;
    fullscreenEditor?: boolean;
    fetchingAnalyses?: boolean;
    enableImageGallery?: boolean;
    postSmartContentEnabled?: boolean;
    translation: TranslatableContent
    customCss?: ICustomCssModelv1;
    smartContentSuggestion: string;
    turnHighlightsOff?: () => void;
    onBodyFocus?: () => void;
    onFullscreen?: () => void;
    getDraftTranslation: (id: string, content: TranslatableContent, outputLCID: string) => Promise<TranslatableContent>;
    onChangeBody: (newBody: string) => void;
    onChangeTitle: (newTitle: string) => void;
    onChangeSummary: (newSummary: string) => void;
    onChangeTranslatedContent:  (newValue: TranslatedContent) => void;
    onShowSmartContentInsert: (highlightedText: string, promptType: PromptTypeEnum) => void;
    clearSmartContentSuggestion?: () => void;
    generateAndOpenSmartContent?: (promptTypeForGeneration: PromptTypeEnum, promptTypeForInsert: PromptTypeEnum, body?: string) => void;
    useLanguagesData: useLanguagesData;
}

/**
 * The post content editor panel
 * - language selector
 * - TranslatableContent editor
 * - this is an authoring v2 component
 */
const PostMainPanel: React.FunctionComponent<IPostMainPanelProps> = ({
    translations,
    postId,
    highlightedText,
    fetchingAnalyses = false,
    enableImageGallery,
    translation,
    customCss,
    smartContentSuggestion,
    postSmartContentEnabled,
    useLanguagesData,
    onBodyFocus,
    onFullscreen,
    turnHighlightsOff,
    getDraftTranslation,
    onChangeTranslatedContent,
    onChangeBody,
    onChangeSummary,
    onChangeTitle,
    onShowSmartContentInsert,
    clearSmartContentSuggestion,
    generateAndOpenSmartContent,
}) => {
    const { defaultLcid, activeLcid, availableLanguages, lcidMappings } = useLanguagesData;
    const emptyTranslatedContent:TranslatedContent = ({[defaultLcid]: {body: "", title: "", description: ""}});
    const [translating, setTranslating] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    
    const translateAllWrapper = async (inputTranslation: TranslatableContent, outputLanguages: ILcidTranslateDetails[]) => {
        return await cognitiveServicesApi.translateAllPostEventLanguages(
                postId || defaultTranslateContentId,
                inputTranslation,
                outputLanguages
        );
    }

    const { isFetchingTranslations: isFetchingTranslation, errorModalStatus, translateAll } = useTranslateAll ({
        translatedContent: translations ? translations[defaultLcid] : DEFAULT_TRANSLATABLE_CONTENT,
        defaultLcid,
        availableLanguages,
        getAllTranslations: translateAllWrapper
    });

    const isTranslating = isFetchingTranslation || translating;
    const activeLanguage = lcidMappings[activeLcid]?.language ?? "";

    const translationHasContent = (lcid: string) =>
        !!translations &&
        !!translations[lcid] &&
        (!!translations[lcid].title || !!translations[lcid].body || !!translations[lcid].description);

    /**
     * Handle translate click
     * - show dialog if translate will replace content
     */
    const onTranslate = async (inputLanguage: string, outputLcid?: string) => {
        if (!translations || !inputLanguage) return;
        
        const languageToTranslateTo = outputLcid ?? activeLcid;
        const outputLanguage = availableLanguages?.find((language: ILanguage) => language.lcid === languageToTranslateTo);
        
        // do the translation and update state
        if (outputLanguage) {

            try {
                setTranslating(true);

                const translation = await getDraftTranslation(
                    postId || defaultTranslateContentId,
                    translations[inputLanguage],
                    outputLanguage.translateAs
                );
                let newTranslations = { ...translations };
                newTranslations[languageToTranslateTo] = {
                    ...translation,
                    body: `<p><em>[${lcidMappings[languageToTranslateTo].autoTranslateText}]</em></p>${translation?.body || ""}`,
                };
                onChangeTranslatedContent(newTranslations);
            }
            catch {
                //Most likely caused by invalid LCID or network error.
                let newTranslations = { ...translations };

                newTranslations[languageToTranslateTo] = {
                    ...DEFAULT_TRANSLATABLE_CONTENT,
                }
                onChangeTranslatedContent( newTranslations);

                setErrorMessage("Something went wrong, please try again.");
            }
            finally {
                setTranslating(false);
            }
        }
    };

    const onTranslateAll = async (outputLanguages: ILcidTranslateDetails[]) => {
        const finishedTranslations = await translateAll(outputLanguages);

        if (finishedTranslations !== null)
        {
            //Apply translations after fetching
            let newTranslations = { ...translations };
            
            outputLanguages.forEach(lang => { 
                const translation = finishedTranslations[lang.lcid];
                if (translation) {
                    newTranslations[lang.lcid] = {
                        ...translation,
                        body: `<p><em>[${lcidMappings[lang.lcid].autoTranslateText}]</em></p>${translation.body}`,
                    };
                }
            });

            onChangeTranslatedContent(newTranslations);
        }
    }

    const requiredTranslationFieldsHasContent = (lcid: string): boolean => !!translations && !!translations[lcid].body && !!translations[lcid].title;

    return (
        <>
            <LoadingOverlay show={isTranslating} />
            <TranslateAllIssueDialog
                show={errorModalStatus.showModal}
                onClose={() => errorModalStatus.updateModalOpenStatus(false)}
                totalLanguages={errorModalStatus.expectedLanguages}
                returnedTranslations={errorModalStatus.actualLanguages}
            />
            <div className="post-main-panel">
                <LanguageBarWithTranslateDialog
                    translations={translations ?? emptyTranslatedContent}
                    defaultContent={DEFAULT_TRANSLATABLE_CONTENT}
                    onChangeTranslatedContent={onChangeTranslatedContent}
                    onTranslate={onTranslate}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left"}}
                    useLanguagesData={useLanguagesData}
                    requiredTranslationFieldsHasContent={requiredTranslationFieldsHasContent}
                    translationHasContent={translationHasContent}
                    onOpen={() => {
                        if (turnHighlightsOff) turnHighlightsOff();
                    }}
                    endIcon={<KeyboardArrowDown />}
                    contentType={TranslationContentType.Post}
                    showTranslateAllOption
                    translateAll={onTranslateAll}
                    translateAllDisabled={!requiredTranslationFieldsHasContent(defaultLcid) || isFetchingTranslation}
                    disableLanguageButtons={isFetchingTranslation}
                />
                <TranslatableContentEditor
                    customCss={customCss}
                    fetchingAnalyses={fetchingAnalyses}
                    activeLcid={activeLcid}
                    activeLanguage={activeLanguage}
                    highlightedText={highlightedText}
                    postSmartContentEnabled={postSmartContentEnabled}
                    onBodyFocus={onBodyFocus}
                    onFullscreen={onFullscreen}
                    onChangeBody={onChangeBody}
                    onChangeSummary={onChangeSummary}
                    onChangeTitle={onChangeTitle}
                    translation={translation}
                    onShowSmartContentInsert={onShowSmartContentInsert}
                    smartContentSuggestion={smartContentSuggestion}
                    clearSmartContentSuggestion={clearSmartContentSuggestion}
                    generateAndOpenSmartContent={generateAndOpenSmartContent}
                    enableImageGallery={enableImageGallery}
                />
                <ErrorSnackbar errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage("")}/>
            </div>
        </>
    );
};

export default PostMainPanel;
